import React, { useCallback, useEffect, useState } from "react";
import Loader from "../../common/components/Loader";
import { analyzeImages, getSavedResult } from "../imageAnalysisService";
import { STATUS_LABEL, UNAVAILABLE_REASON, humanise } from "../constants";
import { useImageAnalysisSettings } from "../ImageAnalysisSettings";
import ModelPicker from "./ModelPicker";
import ReadingHistory from "./ReadingHistory";
import "../imageAnalysis.css";

// The AI Analyze tab of the image viewer, on the Image Analyse page: Otto's SAVED readings of this photo.
//
// It shows what is stored, not a live call - opening the viewer and paging through photos must never spend
// money. A photo with no reading gets an explicit "Analyse this image" button, shown only to users who may
// run analyses (`canAnalyse`, the page's `manage` grant); the backend refuses the call for anyone else anyway.
//
// RE-ANALYSING KEEPS THE OLD READING (the backend stores the newest few). They are rendered by
// `ReadingHistory`, so "we switched model - is it better on our photographs?" is answerable on the photograph
// itself rather than by exporting two tables. THE MODEL IS PICKED HERE TOO, beside the button, because this
// tab is where that question is actually asked: read one photo with the other model and compare the two
// readings without leaving the viewer. It is the same setting the run bar carries (one page, one model), so
// the tab opens on whatever the bar is set to.

function ImageAnalysisFindings({ imageUploadId, canAnalyse = false, onAnalysed }) {
    const { model, models, showPricing } = useImageAnalysisSettings();
    const [loading, setLoading] = useState(true);
    const [analysing, setAnalysing] = useState(false);
    const [record, setRecord] = useState(null);
    const [history, setHistory] = useState([]);
    const [error, setError] = useState("");

    const load = useCallback(async () => {
        if (!imageUploadId) return;
        setLoading(true);
        setError("");
        try {
            const res = await getSavedResult(imageUploadId);
            const data = (res && res.data) || {};
            setRecord(data.record || null);
            setHistory(data.history || []);
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[imageAnalysis] loading saved reading failed", e && e.response ? e.response.status : e);
            setError(e && e.response && e.response.status === 403 ? "You do not have access to image analysis." : "Could not load Otto's findings for this image.");
        } finally {
            setLoading(false);
        }
    }, [imageUploadId]);

    useEffect(() => {
        load();
    }, [load]);

    const analyseNow = async () => {
        setAnalysing(true);
        setError("");
        try {
            const res = await analyzeImages([imageUploadId], { model });
            const data = (res && res.data) || {};
            // The whole capability is unavailable (no S3 credentials, or no imaging library): there are no
            // per-image results to read, and a silent no-op would leave the button looking broken. Reported
            // as what it is - a server problem, not this photograph's.
            if (data.status === "unavailable") {
                setError(UNAVAILABLE_REASON[data.reason] || "Image analysis is unavailable on the server.");
                return;
            }
            const result = data.results && data.results[0];
            if (result && result.record) {
                setRecord(result.record);
                // Re-read rather than pushing the new reading onto the list here: the server decides what the
                // history holds (it keeps the newest few), and guessing that here would drift from it.
                await load();
                if (onAnalysed) onAnalysed(imageUploadId, result.record);
            } else if (result && result.status !== "ok") setError(STATUS_LABEL[result.status] || "Analysis failed.");
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("[imageAnalysis] analyse failed", e && e.response ? { status: e.response.status, data: e.response.data } : e);
            setError(e && e.response && e.response.status === 403 ? "You do not have permission to analyse images." : "Could not analyse this image. Please try again.");
        } finally {
            setAnalysing(false);
        }
    };

    if (loading || analysing) {
        return (
            <div className="ia-tab ia-loading">
                <Loader />
                <div>{analysing ? "Otto is reading this photo…" : "Loading Otto's findings…"}</div>
            </div>
        );
    }

    const hasReading = record && record.status === "ok" && record.analysis;
    // The stored history already contains the current reading, so it is the one thing rendered when there is
    // one - a separate "current" block above it would show the newest reading twice.
    const readings = history.length ? history : hasReading ? [{ ...record, id: "current" }] : [];
    const modelLabel = id => (models.find(m => m.id === id) || {}).label || id;

    return (
        <div className="ia-tab not-dragabble">
            {error && <div className="ia-error">{error}</div>}

            {hasReading ? (
                <ReadingHistory history={readings} showPricing={showPricing} modelLabel={modelLabel} />
            ) : (
                <div className="ia-empty">
                    {record && record.status !== "ok" ? (
                        <div className="ia-status">Last attempt: {STATUS_LABEL[record.status] || humanise(record.status)}</div>
                    ) : (
                        <div className="ia-status">Otto has not analysed this image yet.</div>
                    )}
                </div>
            )}

            {canAnalyse && (
                <div className="ia-analyse-one">
                    <ModelPicker disabled={analysing} />
                    <button type="button" className="btn btn-primary btn-sm" onClick={analyseNow}>
                        {hasReading ? "Re-analyse this image" : "Analyse this image"}
                    </button>
                </div>
            )}
        </div>
    );
}

export default ImageAnalysisFindings;
